<script setup>
import { isBoolean, isEmpty, isNull } from 'lodash'
import { onBeforeUnmount, onUpdated } from 'vue'
import { computed, inject, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { WORKSPACE_ENTITY_KEYS } from '@/utils/entity-keys'
import { listStateInjectionKey, splitViewStateInjectionKey } from '@/utils/injection-keys'
import {
  PROGRESS_VIEW_ENABLED_COLUMNS_BY_DEFAULT,
  ROADMAP_ENABLED_COLUMNS_BY_DEFAULT
} from '@/utils/objective-table'
import { currentUserCanCreateObjective } from '@/utils/objectives'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { updateStorageByKey } from '@/utils/persist'
import { PROGRESS_VIEW_PLACEMENT } from '@/utils/progress-view'
import { ROADMAP_KEY } from '@/utils/roadmap'
import { SPLIT_VIEW_FOOTER_HEIGHT } from '@/utils/split-view'
import {
  getResolvedRestoredValue,
  PROGRESS_VIEW_TABLE_COLLAPSED_STATE,
  PROGRESS_VIEW_TABLE_SIDE_WIDTH,
  ROADMAP_TABLE_COLLAPSED_STATE,
  ROADMAP_TABLE_SIDE_WIDTH,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'

import ObjectiveTable from '@/components/objectives/table/ObjectiveTable'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import TaskIconForExport from '@/components/ui/TaskIconForExport'

defineOptions({
  name: 'SplitView'
})

const props = defineProps({
  placement: {
    type: String,
    required: true,
    validator: v => {
      const isValid = [PROGRESS_VIEW_PLACEMENT, ROADMAP_KEY].includes(v)
      if (!isValid) {
        throw new Error(`Invalid placement prop value: ${v}`)
      }
      return isValid
    }
  },

  showFilterButtons: {
    type: Boolean
  }
})

const COLLAPSED_TABLE_WIDTH = 0
const FOOTER_HEIGHT = SPLIT_VIEW_FOOTER_HEIGHT
const MIN_TABLE_SIDE_WIDTH = 220
const MAX_TABLE_SIDE_WIDTH_IN_PERCENT = 60

const getElementWidth = el => el.getBoundingClientRect().width
const transformPixelsToPercents = (pixels, wrapperWidth) => {
  return (pixels * 100) / wrapperWidth
}

const store = useStore()
const fullscreen = computed(() => store.state.system.fullscreen)
const listState = inject(listStateInjectionKey)

const isNoElements = computed(() => {
  return isEmpty(listState.rootElements) && isEmpty(listState.krs)
})

const wrapperClasses = computed(() => {
  return {
    'sv-Page-resize-continues': splitViewState.resizeContinues,
    'sv-Page-fullscreen': fullscreen.value,
    'sv-Page-no-elements': isNoElements.value
  }
})

const wrapperReference = useTemplateRef('wrapper')

const newTableSideWidth = ref('50%')

const splitViewState = inject(splitViewStateInjectionKey, null)

if (!splitViewState) {
  throw new Error('splitViewState is not provided')
}

const tableSideReference = useTemplateRef('table-side')

const tableReference = useTemplateRef('table')

const okrElementsLevels = computed(() => store.state.objectives.levels)
const selectedWorkspace = computed(() => store.getters['workspaces/selectedWorkspace'])
const isPluginAdmin = computed(() => store.state.pluginOptions.isPluginAdmin)

const userCanCreateObjectives = computed(() => {
  return currentUserCanCreateObjective(selectedWorkspace.value, isPluginAdmin.value)
})

const route = useRoute()

const workspaceId = computed(() => route.params[WORKSPACE_ENTITY_KEYS.WORKSPACE_ID])

const emit = defineEmits({
  'create-objective': null,
  'reset-search-criteria': null,
  'load-more': null,
  'update-data': null,
  'expand-all': null,
  'collapse-all': null,
  'table-resized': null,
  'on-updated-hook-invoked': null
})

const onCreateElementClick = async levelId => {
  emit('create-objective', {
    levelId
  })
}

const requestData = expandAll => {
  tableReference.value.requestData(expandAll)
}

const resizeObserver = ref(null)

const disconnectResizeObserver = () => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
    resizeObserver.value = null
  }
}

const settingsKeys = computed(() => {
  const PROGRESS_LS_TABLE_COLLAPSED_KEY = 'progressTableCollapsed'
  const PROGRESS_LS_TABLE_WIDTH_KEY = 'progressTableWidth'
  const ROADMAP_LS_TABLE_WIDTH_KEY = 'roadmapTableWidth'
  const ROADMAP_LS_IS_TABLE_COLLAPSED_KEY = 'roadmapIsTableCollapsed'

  const keysByPlacement = {
    [PROGRESS_VIEW_PLACEMENT]: {
      lsTableCollapsedKey: PROGRESS_LS_TABLE_COLLAPSED_KEY,
      lsTableWidthKey: PROGRESS_LS_TABLE_WIDTH_KEY,
      settingsTableWidthKey: USER_SETTINGS_MAPPER[PROGRESS_VIEW_TABLE_SIDE_WIDTH],
      settingsTableCollapsedKey: USER_SETTINGS_MAPPER[PROGRESS_VIEW_TABLE_COLLAPSED_STATE]
    },
    [ROADMAP_KEY]: {
      lsTableCollapsedKey: ROADMAP_LS_IS_TABLE_COLLAPSED_KEY,
      lsTableWidthKey: ROADMAP_LS_TABLE_WIDTH_KEY,
      settingsTableWidthKey: USER_SETTINGS_MAPPER[ROADMAP_TABLE_SIDE_WIDTH],
      settingsTableCollapsedKey: USER_SETTINGS_MAPPER[ROADMAP_TABLE_COLLAPSED_STATE]
    }
  }

  const keys = keysByPlacement[props.placement]
  if (!keys) {
    throw new Error(`Invalid placement prop value: ${props.placement}`)
  }
  return keys
})

const initResizeObserver = () => {
  resizeObserver.value = new ResizeObserver(async () => {
    const tableSideWidth = getElementWidth(tableSideReference.value)

    if (tableSideWidth < MIN_TABLE_SIDE_WIDTH) {
      newTableSideWidth.value = `${MIN_TABLE_SIDE_WIDTH}px`
      const width = getElementWidth(tableSideReference.value)
      updateStorageByKey(settingsKeys.value.lsTableWidthKey, width)

      await store.dispatch('system/updateUserSettings', {
        [settingsKeys.value.settingsTableWidthKey]: width
      })
    }
    emit('table-resized')
  })

  resizeObserver.value.observe(tableSideReference.value)
}

const setTableSideCollapsedWidth = () => {
  newTableSideWidth.value = `${COLLAPSED_TABLE_WIDTH}px`
}

const savedSettings = computed(() => {
  const settingsByPlacement = {
    [PROGRESS_VIEW_PLACEMENT]: {
      tableWidthFromSettings: store.getters['system/progressViewTableSideWidth'],
      tableCollapsedStateFromSettings: store.getters['system/progressViewTableCollapsedState']
    },
    [ROADMAP_KEY]: {
      tableWidthFromSettings: store.getters['system/roadmapTableSideWidth'],
      tableCollapsedStateFromSettings: store.getters['system/roadmapTableCollapsedState']
    }
  }

  const settings = settingsByPlacement[props.placement]
  if (!settings) {
    throw new Error(`Invalid placement prop value: ${props.placement}`)
  }
  return settings
})

const restoreTableSideWidth = () => {
  const resolvedRestoredWidth = getResolvedRestoredValue({
    valueFromSettings: savedSettings.value.tableWidthFromSettings,
    localStorageKey: settingsKeys.value.lsTableWidthKey,
    isBooleanOrNumberValueRestores: true
  })

  if (!isNull(resolvedRestoredWidth)) {
    const wrapperWidth = getElementWidth(wrapperReference.value)

    if (resolvedRestoredWidth === MIN_TABLE_SIDE_WIDTH || resolvedRestoredWidth <= 0) {
      newTableSideWidth.value = `${MIN_TABLE_SIDE_WIDTH}px`
      return
    }

    if (resolvedRestoredWidth > MAX_TABLE_SIDE_WIDTH_IN_PERCENT) {
      const widthInPercent = transformPixelsToPercents(resolvedRestoredWidth, wrapperWidth)

      newTableSideWidth.value = `${Math.min(MAX_TABLE_SIDE_WIDTH_IN_PERCENT, widthInPercent)}${
        MATH_SYMBOLS.PERCENT
      }`

      return
    }

    newTableSideWidth.value = `${resolvedRestoredWidth}${MATH_SYMBOLS.PERCENT}`
  }
}

const onCollapseExpandTable = async (isManual = false) => {
  if (!splitViewState.isTableCollapsed) {
    disconnectResizeObserver()
    setTableSideCollapsedWidth()
  } else {
    restoreTableSideWidth()
    initResizeObserver()
  }

  splitViewState.isTableCollapsed = !splitViewState.isTableCollapsed
  if (!isManual) {
    updateStorageByKey(settingsKeys.value.lsTableCollapsedKey, splitViewState.isTableCollapsed)

    await store.dispatch('system/updateUserSettings', {
      [settingsKeys.value.settingsTableCollapsedKey]: splitViewState.isTableCollapsed
    })
  }
}

defineExpose({
  onCollapseExpandTable,
  requestData
})

const restoreTableCollapsedState = () => {
  const resolvedRestoredCollapsedState = getResolvedRestoredValue({
    valueFromSettings: savedSettings.value.tableCollapsedStateFromSettings,
    localStorageKey: settingsKeys.value.lsTableCollapsedKey,
    isBooleanOrNumberValueRestores: true
  })

  if (isBoolean(resolvedRestoredCollapsedState)) {
    splitViewState.isTableCollapsed = resolvedRestoredCollapsedState
    if (resolvedRestoredCollapsedState) {
      setTableSideCollapsedWidth()
    }
  }
}

onMounted(() => {
  restoreTableCollapsedState()

  if (!splitViewState.isTableCollapsed) {
    restoreTableSideWidth()
    initResizeObserver()
  }
})

const mouseX = ref(0)
const tableSideWidth = ref(0)

const onResizerMouseDown = e => {
  if (!splitViewState.isTableCollapsed) {
    mouseX.value = e.clientX
    tableSideWidth.value = getElementWidth(tableSideReference.value)
    splitViewState.resizeContinues = true

    document.addEventListener('mousemove', resizeTable)
    document.addEventListener('mouseup', leaveResizing)
  }
}

const resizeTable = e => {
  const dx = e.clientX - mouseX.value
  const wrapperWidth = getElementWidth(wrapperReference.value)

  const newTableSideWidthInPercent = transformPixelsToPercents(
    tableSideWidth.value + dx,
    wrapperWidth
  )
  const tableSideMinWidthInPercent = transformPixelsToPercents(MIN_TABLE_SIDE_WIDTH, wrapperWidth)

  const isNewWidthBetweenMinMax =
    newTableSideWidthInPercent >= tableSideMinWidthInPercent &&
    newTableSideWidthInPercent <= MAX_TABLE_SIDE_WIDTH_IN_PERCENT

  const [updatedTableSideWidth] = [
    isNewWidthBetweenMinMax && `${newTableSideWidthInPercent}${MATH_SYMBOLS.PERCENT}`,
    newTableSideWidthInPercent < tableSideMinWidthInPercent && `${MIN_TABLE_SIDE_WIDTH}px`,
    newTableSideWidthInPercent > MAX_TABLE_SIDE_WIDTH_IN_PERCENT &&
      `${MAX_TABLE_SIDE_WIDTH_IN_PERCENT}${MATH_SYMBOLS.PERCENT}`
  ].filter(Boolean)

  newTableSideWidth.value = updatedTableSideWidth
}

const leaveResizing = async () => {
  document.removeEventListener('mousemove', resizeTable)
  splitViewState.resizeContinues = false
  const width = getElementWidth(tableSideReference.value)

  const widthForSave =
    width === MIN_TABLE_SIDE_WIDTH
      ? MIN_TABLE_SIDE_WIDTH
      : transformPixelsToPercents(width, getElementWidth(wrapperReference.value))

  updateStorageByKey(settingsKeys.value.lsTableWidthKey, widthForSave)

  await store.dispatch('system/updateUserSettings', {
    [settingsKeys.value.settingsTableWidthKey]: widthForSave
  })
  document.removeEventListener('mouseup', leaveResizing)
}

const { t } = useI18n()

const tooltipText = computed(() => {
  return splitViewState.isTableCollapsed ? t('menu.tooltip.expand') : t('menu.tooltip.collapse')
})

const enabledColumnsByDefault = computed(() => {
  const defaultColumnsByPlacement = {
    [PROGRESS_VIEW_PLACEMENT]: [...PROGRESS_VIEW_ENABLED_COLUMNS_BY_DEFAULT],
    [ROADMAP_KEY]: [...ROADMAP_ENABLED_COLUMNS_BY_DEFAULT]
  }

  return defaultColumnsByPlacement[props.placement] || []
})

onBeforeUnmount(() => {
  disconnectResizeObserver()
})

onUpdated(() => {
  emit('on-updated-hook-invoked')
})
</script>

<template>
  <div
    ref="wrapper"
    :class="wrapperClasses"
    :style="{
      '--sv-table-width': newTableSideWidth,
      '--sv-footer-height': `${FOOTER_HEIGHT}px`
    }"
    class="sv-Page"
  >
    <div
      ref="table-side"
      :class="{ 'sv-Page_Table-collapsed': splitViewState.isTableCollapsed }"
      class="sv-Page_Table"
    >
      <ObjectiveTable
        ref="table"
        :enabled-columns-by-default
        :objective-levels="okrElementsLevels"
        :show-filter-buttons
        :table-placement="placement"
        :user-can-create-objectives
        :workspace-id
        first-level-draggable
        header-height="56px"
        hide-bulk-actions
        no-padding-bottom
        offset-left="var(--page-left-padding)"
        offset-right="var(--page-right-padding)"
        resizeable-columns
        tab-name="Alignment"
        transparent-filtered
        @create-objective="onCreateElementClick($event.typeId)"
        @reset-search-criteria="emit('reset-search-criteria', $event)"
        @load-more="emit('load-more', $event)"
        @update-okr-data="emit('update-data', $event)"
        @expand-all="emit('expand-all')"
        @collapse-all="emit('collapse-all')"
      />
    </div>

    <div
      v-if="splitViewState.isTableCollapsed && !isNoElements"
      class="sv-CollapsedTablePlug"
      @click="onCollapseExpandTable(false)"
    >
      <div class="sv-CollapsedTablePlug_Text">
        {{ $t('group.table_view') }}
      </div>
    </div>

    <div
      v-show="!isNoElements"
      :class="{ 'sv-Page_Resizer-collapsed-table': splitViewState.isTableCollapsed }"
      class="sv-Page_Resizer"
      @mousedown.self="onResizerMouseDown"
    >
      <div class="sv-BackButtonWrapper">
        <AppButton
          v-tippy="{
            content: tooltipText,
            placement: 'right'
          }"
          class="sv-BackButton"
          remove-padding
          type="primary-next"
          @click="onCollapseExpandTable(false)"
        >
          <AppIcon
            class="sv-BackButton_Icon"
            height="24"
            icon-name="roadmap-collapse-table"
            width="24"
          />
        </AppButton>
      </div>
      <span class="sv-PageShadow" />
    </div>

    <slot :is-no-elements="isNoElements" name="content-panel" />

    <div v-if="!isNoElements" class="sv-Page_Footer" data-export-ignore>
      <div class="sv-PageFooterRight">
        <TaskIconForExport />

        <slot :is-no-elements="isNoElements" name="footer-actions" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/roadmap';
@import '~@/assets/styles/canvas-dimensions';

.sv-Page {
  display: flex;
  min-height: calc(#{$default-canvas-height} - var(--sv-footer-height));
  padding-bottom: var(--sv-footer-height);

  &-no-elements {
    justify-content: center;
  }

  &-fullscreen {
    min-height: calc(#{$fullscreen-canvas-height} - var(--sv-footer-height));
  }
}

/* eslint-disable-next-line */
%interaction-remove-styles {
  pointer-events: none;
  user-select: none;
}

.sv-Page_Table {
  z-index: 1;
  position: relative;
  width: var(--sv-table-width);
  // box-shadow: inset 0 1px 0 $grey-medium;
  // padding: 0 $page-right-padding 0 $page-left-padding;
  padding: 0 $page-right-padding 0 $page-left-padding;

  .sv-Page-no-elements & {
    width: 100%;
  }

  .sv-Page-resize-continues & {
    @extend %interaction-remove-styles;
  }

  &-collapsed {
    padding: 0;
    overflow: hidden;
  }
}

/* eslint-disable-next-line */
%collapse-table-offset-top {
  top: calc(
    var(--objectives-page-header-height, #{$default-objectives-page-header-height}) +
      var(--app-license-banner-height, 0px) + #{$timelines-head-height}
  );
  .sv-Page-fullscreen & {
    top: calc(
      var(--objectives-page-header-height, #{$default-objectives-page-header-height}) +
        var(--app-license-banner-height, 0px)
    );
  }
}

.sv-BackButtonWrapper {
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%);
  width: 32px;
  height: calc(#{$default-canvas-height} - var(--sv-footer-height));
  display: flex;
  align-items: center;
  position: sticky;
  @extend %collapse-table-offset-top;

  .sv-Page-fullscreen & {
    height: calc(#{$fullscreen-canvas-height} - var(--sv-footer-height));
  }
}

.sv-BackButton {
  width: 32px;

  align-items: center;
  transition: background $transition-normal, color $transition-normal;

  &:not(.sv-Page-fullscreen &) {
    // necessary to keep this position in center of block when el stuck to the top
    margin-bottom: calc((#{$head-navigations} / 2) - (var(--sv-footer-height) / 2));
  }
}

.sv-BackButton_Icon {
  .sv-Page_Resizer-collapsed-table & {
    transform: rotate(180deg);
  }
}

/* eslint-disable-next-line */
%resizer-line {
  background: linear-gradient(
    to right,
    transparent 0%,
    transparent 80%,
    $primary-color-next 80%,
    $primary-color-next 100%
  );
}

.sv-CollapsedTablePlug {
  width: $page-left-padding;
  flex: 0 0 $page-left-padding;
  //position: sticky;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  //padding-bottom: 80px;

  color: $placeholder-color;

  border-right: 2px solid $grey-2-next;

  // @extend %collapse-table-offset-top;

  height: auto;
  //height: calc(
  //  100vh - var(--objectives-page-header-height) - var(--app-license-banner-height) - #{$jira-elements-height} -
  //    var(--sv-footer-height)
  //);
  //
  //top: calc(
  //  var(--objectives-page-header-height, #{$default-objectives-page-header-height}) +
  //    var(--app-license-banner-height, 0px) + #{$jira-elements-height}
  //);

  //.sv-Page-fullscreen & {
  //  height: calc(#{$fullscreen-canvas-height} - var(--sv-footer-height));
  //}

  cursor: pointer;

  @media (any-hover: hover) {
    &:hover {
      color: $blue-1;
    }
  }

  &:hover + .sv-Page_Resizer {
    .sv-BackButtonWrapper {
      opacity: 1;
      z-index: 4;
    }

    &:before {
      @extend %resizer-line;
    }
  }
}

.sv-CollapsedTablePlug_Text {
  text-transform: uppercase;
  font-weight: fw('medium');
  font-size: $fs-14;
  transform: rotate(-180deg);
  writing-mode: vertical-rl;
  position: sticky;
  bottom: calc(var(--sv-footer-height) + 6px);
}

.sv-Page_Resizer {
  width: 0;
  background-color: transparent;

  position: relative;
  z-index: 4;

  @media (any-hover: hover) {
    &:hover {
      .sv-BackButtonWrapper {
        opacity: 1;
        z-index: 4;
      }

      .sv-BackButton {
        pointer-events: all;
      }
    }
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 0;
    height: 100%;
    width: 10px;
    background-color: transparent;
  }
  &:before {
    left: 0;
    transform: translateX(-100%);
  }

  &:after {
    right: 0;
    transform: translateX(100%);
  }
  &:hover,
  &:active {
    &:before {
      @extend %resizer-line;
    }
  }

  &:not(&-collapsed-table) {
    cursor: ew-resize;
    &:before,
    &:after {
      cursor: ew-resize;
    }
  }
}

.sv-PageShadow {
  position: absolute;
  height: 100%;
  width: 10px;
  right: 0;
  left: unset;
  top: 0;
  pointer-events: none;
  box-shadow: 8px 0 10px rgba($dark-1, 0.15);
}

.sv-Page_Footer {
  position: fixed;
  height: var(--sv-footer-height);
  bottom: 0;
  right: 0;
  width: calc(100% - var(--ob-collapsed-app-menu-width));
  background: $white;
  box-shadow: inset 0 2px 0 $grey-2-next;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 $page-right-padding 0 $page-left-padding;

  .app-App-menu-expanded & {
    width: calc(100% - var(--ob-expanded-app-menu-width));
  }

  .sv-Page-fullscreen & {
    width: 100%;
  }
}

.sv-PageFooterRight {
  margin-left: auto;
}
</style>
